import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';
import { FindStorePosProductResponseDto } from '../../../core/stores/dtos/partials/find-store-pos-product.response.dto';
import { FindStorePosCategoryResponseDto } from '../../../core/stores/dtos/partials/find-store-pos-category.response.dto';
import { ApplicableDiscount } from '../../../model/discount-applicable';
import { LatestCashRegisterSession } from '../../../model/cash-register-session';
import { DiscountType } from '../../../model/enum/discount-type';
import { FindStoreInvoiceMetadataResponseDto } from '../../../core/stores/dtos/find-store-invoice-metadata.response.dto';
import { StoreTaxRegistrationStatus } from '../../../core/stores/enums/store-tax-registration-status.enum';
import { FindRegisterInvoiceSeriesResponseDto } from '../../cash-drawers/dtos/find-cash-register-invoice-series.response.dto';
import { FindCustomerNamesResponseDto } from '../../customers/dtos/find-customer-names.response.dto';
import { CurrentTransactionSerializedItemEntry } from '../current-transaction/types/current-transaction';
import { BaseCashRegisterSessionResponseDto } from '../../cash-drawers/cash-register-session/dtos/partials/base-cash-register-session.response.dto';
import { FindLatestCashRegisterSessionResponseDto } from '../../cash-drawers/cash-register-session/dtos/find-latest-cash-register-session.response.dto';

export interface PosProduct extends FindStorePosProductResponseDto {
  storeId: number;
  lastUpdated: number;
}

export interface PosCategory extends FindStorePosCategoryResponseDto {
  storeId: number;
  lastUpdated: number;
}

export interface PosTransactionInvoice {
  invoiceNumber: string;
  type: 'original' | 'internal';
  rawInvoiceNumber: number;
  resetCounter: number | null;
  includeBuyerInfo: boolean;
  cashReceived: number;
}

export interface PosTransaction {
  id?: number;
  items: CurrentTransactionSerializedItemEntry[];
  discount: any;
  cashReceived: number;
  expectedTotalPayable: number;
  bnpcRemainingBalanceThisWeek?: number;
  bnpcItemsCountThisWeek?: number;
  paymentMethod: string;
  customer: any;
  uuid?: string;
  status: string;
  synced: number;
  createdAt: number;
  createdWhileOffline?: boolean;
  storeId: number;
  registerId: number;
  staffId?: number;
  type?: string;
  note?: string;
  tip?: number;
  serviceChargeRate?: number;
  salesTaxRate?: number;
  orderNumber?: string;
  invoice?: PosTransactionInvoice;
}

export interface PosMetadata {
  key: string;
  value: any;
  lastUpdated: number;
}

export interface PosDiscount extends ApplicableDiscount {
  storeId: number;
  minAmount: number;
  maxAmount: number;
  customerId?: number;
  lastUpdated: number;
}

type BasePosCustomer = FindCustomerNamesResponseDto['data'][0];
export interface PosCustomer extends BasePosCustomer {
  storeId: number;
  lastUpdated: number;
  searchTerms: string;
}

export interface PosRegisterSession {
  registerId: number;
  storeId: number;
  session: BaseCashRegisterSessionResponseDto;
  lastUpdated: number;
}

export interface PosProductStock {
  productId: number;
  storeId: number;
  stock: number | null;
  lastUpdated: number;
}

export interface PosParkedTransaction {
  uuid: string;
  storeId: number;
  registerId: number;
  transactionData: any;
  lastUpdated: number;
}

export interface PosOfflineOperation {
  id?: number;
  type:
    | 'OPEN_REGISTER'
    | 'CLOSE_REGISTER'
    | 'ADD_CASH_MOVEMENT'
    | 'EDIT_CASH_MOVEMENT'
    | 'DELETE_CASH_MOVEMENT'
    | 'CREATE_CUSTOMER';
  registerId: number;
  storeId: number;
  data: any;
  synced: number;
  createdAt: number;
  tempId?: string;
}

export interface PosRegisterList {
  id: string;
  registers: any[];
  lastUpdated: number;
}

export interface PosStoreList {
  id: string;
  stores: any[];
  lastUpdated: number;
}

export interface PosOrderSequence {
  registerId: number;
  sequence: number;
  lastUpdated: number;
}

export const DEVICE_CREDENTIALS_RECORD_ID = 'current' as const;

export interface PosDeviceCredentials {
  id: typeof DEVICE_CREDENTIALS_RECORD_ID;
  cashRegisterId: number;
  privateKey: CryptoKey;
  publicKey: CryptoKey;
}

@Injectable({
  providedIn: 'root',
})
export class PosOfflineDbService extends Dexie {
  products!: Table<PosProduct, number>;
  categories!: Table<PosCategory, number>;
  transactions!: Table<PosTransaction, number>;
  metadata!: Table<PosMetadata, string>;
  discounts!: Table<PosDiscount, number>;
  customers!: Table<PosCustomer, number>;
  registerSessions!: Table<PosRegisterSession, number>;
  productStocks!: Table<PosProductStock, number>;
  parkedTransactions!: Table<PosParkedTransaction, string>;
  offlineOperations!: Table<PosOfflineOperation, number>;
  registerList!: Table<PosRegisterList, string>;
  storeList!: Table<PosStoreList, string>;
  orderSequences!: Table<PosOrderSequence, number>;
  deviceCredentials!: Table<PosDeviceCredentials, string>;

  constructor() {
    super('OrdernaPosDb');

    this.version(1).stores({
      products: 'id, storeId, categoryId, lastUpdated',
      categories: 'id, storeId, lastUpdated',
      transactions: '++id, storeId, registerId, synced, createdAt',
      metadata: 'key, lastUpdated',
    });

    this.version(2).stores({
      products: 'id, storeId, categoryId, lastUpdated',
      categories: 'id, storeId, lastUpdated',
      transactions: '++id, storeId, registerId, synced, createdAt',
      metadata: 'key, lastUpdated',
      discounts: 'id, storeId, minAmount, maxAmount, customerId, lastUpdated',
      customers: 'value, storeId, searchTerms, lastUpdated',
      registerSessions: 'registerId, storeId, lastUpdated',
      productStocks: 'productId, storeId, lastUpdated',
      parkedTransactions: 'uuid, storeId, registerId, lastUpdated',
    });

    this.version(3).stores({
      products: 'id, storeId, categoryId, lastUpdated',
      categories: 'id, storeId, lastUpdated',
      transactions: '++id, storeId, registerId, synced, createdAt',
      metadata: 'key, lastUpdated',
      discounts: 'id, storeId, minAmount, maxAmount, customerId, lastUpdated',
      customers: 'value, storeId, searchTerms, lastUpdated',
      registerSessions: 'registerId, storeId, lastUpdated',
      productStocks: 'productId, storeId, lastUpdated',
      parkedTransactions: 'uuid, storeId, registerId, lastUpdated',
      offlineOperations:
        '++id, registerId, storeId, type, synced, createdAt, tempId',
      registerList: 'id, lastUpdated',
      storeList: 'id, lastUpdated',
    });

    this.version(4).stores({
      products: 'id, storeId, categoryId, lastUpdated',
      categories: 'id, storeId, lastUpdated',
      transactions: '++id, storeId, registerId, synced, createdAt',
      metadata: 'key, lastUpdated',
      discounts: 'id, storeId, minAmount, maxAmount, customerId, lastUpdated',
      customers: 'value, storeId, searchTerms, lastUpdated',
      registerSessions: 'registerId, storeId, lastUpdated',
      productStocks: 'productId, storeId, lastUpdated',
      parkedTransactions: 'uuid, storeId, registerId, lastUpdated',
      offlineOperations:
        '++id, registerId, storeId, type, synced, createdAt, tempId',
      registerList: 'id, lastUpdated',
      storeList: 'id, lastUpdated',
      orderSequences: 'registerId, lastUpdated',
    });

    this.version(5).stores({
      products: 'id, storeId, categoryId, lastUpdated',
      categories: 'id, storeId, lastUpdated',
      transactions: '++id, storeId, registerId, synced, createdAt',
      metadata: 'key, lastUpdated',
      discounts: 'id, storeId, minAmount, maxAmount, customerId, lastUpdated',

      // NOTE: Drop customers table because we are changing its primary key.
      // Dexie does NOT support altering primary keys in-place, so we must remove
      // and recreate the store in a new version.
      customers: null,

      registerSessions: 'registerId, storeId, lastUpdated',
      productStocks: 'productId, storeId, lastUpdated',
      parkedTransactions: 'uuid, storeId, registerId, lastUpdated',
      offlineOperations:
        '++id, registerId, storeId, type, synced, createdAt, tempId',
      registerList: 'id, lastUpdated',
      storeList: 'id, lastUpdated',
      orderSequences: 'registerId, lastUpdated',
    });

    this.version(6).stores({
      products: 'id, storeId, categoryId, lastUpdated',
      categories: 'id, storeId, lastUpdated',
      transactions: '++id, storeId, registerId, synced, createdAt',
      metadata: 'key, lastUpdated',
      discounts: 'id, storeId, minAmount, maxAmount, customerId, lastUpdated',

      // Recreate customers with new primary key (id instead of legacy "value").
      // This is a clean rebuild of the store after removing the old schema in v5.
      customers: 'id, storeId, searchTerms, lastUpdated', // changed

      registerSessions: 'registerId, storeId, lastUpdated',
      productStocks: 'productId, storeId, lastUpdated',
      parkedTransactions: 'uuid, storeId, registerId, lastUpdated',
      offlineOperations:
        '++id, registerId, storeId, type, synced, createdAt, tempId',
      registerList: 'id, lastUpdated',
      storeList: 'id, lastUpdated',
      orderSequences: 'registerId, lastUpdated',
    });

    this.version(7).stores({
      products: 'id, storeId, categoryId, lastUpdated',
      categories: 'id, storeId, lastUpdated',
      transactions: '++id, storeId, registerId, synced, createdAt',
      metadata: 'key, lastUpdated',
      discounts: 'id, storeId, minAmount, maxAmount, customerId, lastUpdated',
      customers: 'id, storeId, searchTerms, lastUpdated',
      registerSessions: 'registerId, storeId, lastUpdated',
      productStocks: 'productId, storeId, lastUpdated',
      parkedTransactions: 'uuid, storeId, registerId, lastUpdated',
      offlineOperations:
        '++id, registerId, storeId, type, synced, createdAt, tempId',
      registerList: 'id, lastUpdated',
      storeList: 'id, lastUpdated',
      orderSequences: 'registerId, lastUpdated',
      deviceCredentials: 'id',
    });
  }

  async saveProducts(
    storeId: number,
    products: FindStorePosProductResponseDto[],
  ): Promise<void> {
    const timestamp = Date.now();
    const productsWithMeta: PosProduct[] = products.map((p) => ({
      ...p,
      storeId,
      lastUpdated: timestamp,
    }));

    await this.products.bulkPut(productsWithMeta);
  }

  async saveCategories(
    storeId: number,
    categories: FindStorePosCategoryResponseDto[],
  ): Promise<void> {
    const timestamp = Date.now();
    const categoriesWithMeta: PosCategory[] = categories.map((c) => ({
      ...c,
      storeId,
      lastUpdated: timestamp,
    }));

    await this.categories.bulkPut(categoriesWithMeta);
  }

  async getProducts(
    storeId: number,
  ): Promise<FindStorePosProductResponseDto[]> {
    const products = await this.products
      .where('storeId')
      .equals(storeId)
      .toArray();
    return products.map(({ storeId, lastUpdated, ...product }) => product);
  }

  async getCategories(
    storeId: number,
  ): Promise<FindStorePosCategoryResponseDto[]> {
    const categories = await this.categories
      .where('storeId')
      .equals(storeId)
      .toArray();
    return categories.map(({ storeId, lastUpdated, ...category }) => category);
  }

  async saveTransaction(
    transaction: Omit<PosTransaction, 'id'>,
  ): Promise<number> {
    return await this.transactions.add(transaction as PosTransaction);
  }

  async getUnsyncedTransactions(
    offlineOnly: boolean = true,
  ): Promise<PosTransaction[]> {
    const unsyncedTransactions = await this.transactions
      .where('synced')
      .equals(0)
      .toArray();

    if (offlineOnly) {
      return unsyncedTransactions.filter(
        (tx) => tx.createdWhileOffline === true,
      );
    }

    return unsyncedTransactions;
  }

  async markTransactionAsSynced(id: number): Promise<void> {
    await this.transactions.update(id, { synced: 1 });
  }

  async saveMetadata(key: string, value: any): Promise<void> {
    await this.metadata.put({
      key,
      value,
      lastUpdated: Date.now(),
    });
  }

  async getMetadata(key: string): Promise<any> {
    const meta = await this.metadata.get(key);
    return meta?.value;
  }

  async clearStoreData(storeId: number): Promise<void> {
    await this.products.where('storeId').equals(storeId).delete();
    await this.categories.where('storeId').equals(storeId).delete();
  }

  async saveDiscounts(
    storeId: number,
    discounts: ApplicableDiscount[],
    minAmount: number,
    maxAmount: number,
    customerId?: number,
  ): Promise<void> {
    const timestamp = Date.now();
    const discountsWithMeta: PosDiscount[] = discounts.map((d) => ({
      ...d,
      storeId,
      minAmount,
      maxAmount,
      customerId,
      lastUpdated: timestamp,
    }));

    await this.discounts.bulkPut(discountsWithMeta);
  }

  async getDiscounts(
    storeId: number,
    amount: number,
    customerId?: number,
  ): Promise<ApplicableDiscount[]> {
    let query = this.discounts.where('storeId').equals(storeId);
    const allDiscounts = await query.toArray();
    const filtered = allDiscounts.filter((d) => {
      const customerMatch = !d.customerId || d.customerId === customerId;
      return customerMatch;
    });

    const uniqueDiscounts = filtered.reduce((acc, discount) => {
      if (!acc.has(discount.id)) {
        acc.set(discount.id, discount);
      }
      return acc;
    }, new Map<number, PosDiscount>());

    return Array.from(uniqueDiscounts.values()).map(
      ({
        storeId,
        minAmount,
        maxAmount,
        customerId,
        lastUpdated,
        ...discount
      }) => {
        let discountedAmount: number;
        if (discount.type === DiscountType.PERCENTAGE) {
          discountedAmount = amount * (discount.value / 100);
        } else {
          discountedAmount = Math.min(discount.value, amount);
        }
        const amountAfterDiscount = Math.max(0, amount - discountedAmount);

        return {
          ...discount,
          discountedAmount,
          amountAfterDiscount,
        };
      },
    );
  }

  async saveCustomers(
    storeId: number,
    customers: BasePosCustomer[],
  ): Promise<void> {
    const timestamp = Date.now();
    const customersWithMeta: PosCustomer[] = customers.map((c) => ({
      ...c,
      storeId,
      searchTerms: `${c.fullName}`.toLowerCase(),
      lastUpdated: timestamp,
    }));

    await this.customers.bulkPut(customersWithMeta);
  }

  async searchCustomers(
    storeId: number,
    searchTerm: string,
  ): Promise<BasePosCustomer[]> {
    const lowerSearch = searchTerm.toLowerCase();
    const customers = await this.customers
      .where('storeId')
      .equals(storeId)
      .toArray();

    const filtered = customers.filter((c) =>
      c.searchTerms.includes(lowerSearch),
    );
    return filtered.map(
      ({ storeId, searchTerms, lastUpdated, ...customer }) => customer,
    );
  }

  async getAllCustomers(storeId: number): Promise<BasePosCustomer[]> {
    const customers = await this.customers
      .where('storeId')
      .equals(storeId)
      .toArray();
    return customers.map(
      ({ storeId, searchTerms, lastUpdated, ...customer }) => customer,
    );
  }

  async saveRegisterSession(
    registerId: number,
    storeId: number,
    session: BaseCashRegisterSessionResponseDto,
  ): Promise<void> {
    await this.registerSessions.put({
      registerId,
      storeId,
      session,
      lastUpdated: Date.now(),
    });

    if (
      session.register?.machineIdentificationNumber ||
      session.register?.serialNumber
    ) {
      await this.updateRegisterMetaInCache(
        registerId,
        session.register.machineIdentificationNumber!,
        session.register.serialNumber!,
      );
    }
  }

  async updateRegisterMetaInCache(
    registerId: number,
    machineIdentificationNumber?: string,
    serialNumber?: string,
  ): Promise<void> {
    const registers = await this.getRegisterList();
    if (!registers) return;

    const updatedRegisters = registers.map((r: any) => {
      if (r.id === registerId) {
        return {
          ...r,
          ...(machineIdentificationNumber
            ? { machineIdentificationNumber }
            : {}),
          ...(serialNumber ? { serialNumber } : {}),
        };
      }
      return r;
    });

    await this.saveRegisterList(updatedRegisters);
  }

  async getRegisterSession(
    registerId: number,
  ): Promise<BaseCashRegisterSessionResponseDto | null> {
    const record = await this.registerSessions.get(registerId);
    return record?.session ?? null;
  }

  async clearRegisterSession(registerId: number): Promise<void> {
    await this.registerSessions.delete(registerId);
  }

  async saveProductStocks(
    storeId: number,
    stocks: { productId: number; stock: number | null }[],
  ): Promise<void> {
    const timestamp = Date.now();
    const stocksWithMeta: PosProductStock[] = stocks.map((s) => ({
      ...s,
      storeId,
      lastUpdated: timestamp,
    }));

    await this.productStocks.bulkPut(stocksWithMeta);
  }

  async getProductStock(productId: number): Promise<number | null> {
    const record = await this.productStocks.get(productId);
    return record?.stock ?? null;
  }

  async getAllProductStocks(
    storeId: number,
  ): Promise<Map<number, number | null>> {
    const stocks = await this.productStocks
      .where('storeId')
      .equals(storeId)
      .toArray();
    const map = new Map<number, number | null>();
    stocks.forEach((s) => map.set(s.productId, s.stock));
    return map;
  }

  async saveParkedTransaction(
    uuid: string,
    storeId: number,
    registerId: number,
    transactionData: any,
  ): Promise<void> {
    await this.parkedTransactions.put({
      uuid,
      storeId,
      registerId,
      transactionData,
      lastUpdated: Date.now(),
    });
  }

  async getParkedTransactions(
    storeId: number,
    registerId: number,
  ): Promise<PosParkedTransaction[]> {
    const result = await this.parkedTransactions
      .where('storeId')
      .equals(storeId)
      .and((t) => t.registerId === registerId)
      .toArray();

    return result;
  }

  async getParkedTransaction(uuid: string): Promise<any | null> {
    const record = await this.parkedTransactions.get(uuid);
    return record?.transactionData ?? null;
  }

  async removeParkedTransaction(uuid: string): Promise<void> {
    await this.parkedTransactions.delete(uuid);
  }

  async saveOfflineOperation(
    operation: Omit<PosOfflineOperation, 'id'>,
  ): Promise<number> {
    return await this.offlineOperations.add(operation as PosOfflineOperation);
  }

  async getUnsyncedOperations(): Promise<PosOfflineOperation[]> {
    return await this.offlineOperations
      .where('synced')
      .equals(0)
      .sortBy('createdAt');
  }

  async markOperationAsSynced(id: number): Promise<void> {
    await this.offlineOperations.update(id, { synced: 1 });
  }

  async deleteOperation(id: number): Promise<void> {
    await this.offlineOperations.delete(id);
  }

  async getOperationsByTempId(tempId: string): Promise<PosOfflineOperation[]> {
    return await this.offlineOperations
      .where('tempId')
      .equals(tempId)
      .toArray();
  }

  async updateRegisterSessionLocally(
    registerId: number,
    storeId: number,
    registerSession: Partial<FindLatestCashRegisterSessionResponseDto>,
  ): Promise<void> {
    const existing = await this.registerSessions.get(registerId);
    if (existing) {
      await this.registerSessions.put({
        registerId,
        storeId,
        session: { ...existing.session, ...registerSession },
        lastUpdated: Date.now(),
      });
    }
  }

  async saveRegisterList(registers: any[]): Promise<void> {
    await this.registerList.put({
      id: 'registers',
      registers,
      lastUpdated: Date.now(),
    });
  }

  async getRegisterList(): Promise<any[] | null> {
    const record = await this.registerList.get('registers');
    return record?.registers ?? null;
  }

  /**
   * Update the usedByYou flag for a specific register in the cached list
   */
  async updateRegisterUsedByYou(
    registerId: number,
    usedByYou: boolean,
  ): Promise<void> {
    const registers = await this.getRegisterList();
    if (!registers) return;

    const updatedRegisters = registers.map((r: any) => {
      if (r.id === registerId) {
        return { ...r, usedByYou };
      }
      // If setting usedByYou to true for one register, set others to false
      if (usedByYou && r.usedByYou) {
        return { ...r, usedByYou: false };
      }
      return r;
    });

    await this.saveRegisterList(updatedRegisters);
  }

  async saveStoreList(stores: any[]): Promise<void> {
    await this.storeList.put({
      id: 'stores',
      stores,
      lastUpdated: Date.now(),
    });
  }

  async getStoreList(): Promise<any[] | null> {
    const record = await this.storeList.get('stores');
    return record?.stores ?? null;
  }

  async savePrinterSettings(
    storeId: number,
    printerSettings: any,
  ): Promise<void> {
    await this.saveMetadata(`printer_settings_${storeId}`, printerSettings);
  }

  async getPrinterSettings(storeId: number): Promise<any | null> {
    return await this.getMetadata(`printer_settings_${storeId}`);
  }

  async saveSelectedStoreInvoiceMetadata(
    metadata: FindStoreInvoiceMetadataResponseDto,
  ): Promise<void> {
    await this.saveMetadata(`selected_store_invoice_metadata`, metadata);
  }

  async getSelectedStoreInvoiceMetadata(): Promise<FindStoreInvoiceMetadataResponseDto | null> {
    return await this.getMetadata(`selected_store_invoice_metadata`);
  }

  async getSelectedStoreTaxRegistrationStatus(): Promise<StoreTaxRegistrationStatus> {
    const storeInvoiceMetadata = await this.getSelectedStoreInvoiceMetadata();
    return (
      storeInvoiceMetadata?.taxRegistrationStatus ??
      StoreTaxRegistrationStatus.NONE
    );
  }

  async getSelectedStoreServiceChargeRate(): Promise<number | undefined> {
    const storeInvoiceMetadata = await this.getSelectedStoreInvoiceMetadata();
    return storeInvoiceMetadata?.serviceCharge;
  }

  /**
   * Get current order sequence for a register and increment it atomically
   */
  async getAndIncrementOrderSequence(registerId: number): Promise<number> {
    return await this.transaction('rw', this.orderSequences, async () => {
      const existing = await this.orderSequences.get(registerId);
      const currentSequence = existing?.sequence ?? 0;
      const nextSequence = currentSequence + 1;

      await this.orderSequences.put({
        registerId,
        sequence: nextSequence,
        lastUpdated: Date.now(),
      });

      return nextSequence;
    });
  }

  /**
   * Reset order sequence for a register (typically when register is opened)
   */
  async resetOrderSequence(registerId: number): Promise<void> {
    await this.orderSequences.put({
      registerId,
      sequence: 0,
      lastUpdated: Date.now(),
    });
  }

  /**
   * Get current order sequence without incrementing
   */
  async getCurrentOrderSequence(registerId: number): Promise<number> {
    const existing = await this.orderSequences.get(registerId);
    return existing?.sequence ?? 0;
  }

  async saveInvoiceSeries(
    registerId: number,
    series: FindRegisterInvoiceSeriesResponseDto,
  ): Promise<void> {
    await this.saveMetadata(`invoice_series_${registerId}`, series);
  }

  async getInvoiceSeries(
    registerId: number,
  ): Promise<FindRegisterInvoiceSeriesResponseDto | null> {
    return await this.getMetadata(`invoice_series_${registerId}`);
  }

  async getNextInvoiceNumber(registerId: number): Promise<{
    invoiceNumber: number;
    resetCounter: number | null;
    isOriginal: boolean;
  } | null> {
    return await this.transaction('rw', this.metadata, async () => {
      const series: FindRegisterInvoiceSeriesResponseDto | null =
        await this.getMetadata(`invoice_series_${registerId}`);
      if (!series) return null;

      const isOriginal =
        series.resetCounter != null &&
        series.invoiceStartNumber != null &&
        series.invoiceEndNumber != null;

      let nextNumber: number;
      let resetCounter = series.resetCounter ?? null;

      if (isOriginal) {
        if (series.lastInvoiceNumber + 1 > series.invoiceEndNumber!) {
          nextNumber = 1;
          resetCounter = (resetCounter ?? 0) + 1;
        } else {
          nextNumber = series.lastInvoiceNumber + 1;
        }
      } else {
        nextNumber = series.lastInvoiceNumber + 1;
      }

      const updatedSeries: FindRegisterInvoiceSeriesResponseDto = {
        ...series,
        lastInvoiceNumber: nextNumber,
        resetCounter,
      };
      await this.saveMetadata(`invoice_series_${registerId}`, updatedSeries);

      return { invoiceNumber: nextNumber, resetCounter, isOriginal };
    });
  }


}
